import { useCallback, useEffect, useSyncExternalStore } from 'react';

type IncomingMessageSoundMode = 'sound' | 'muted';

const SOUND_MODE_STORAGE_KEY = 'sound-mode';
const DEFAULT_INCOMING_MESSAGE_SOUND_ENABLED = true;

const isBrowser = () => typeof window !== 'undefined';

const isIncomingMessageSoundMode = (
  value: string | null
): value is IncomingMessageSoundMode => value === 'sound' || value === 'muted';

const readLocalStorageItem = (key: string): string | null => {
  if (!isBrowser()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorageItem = (key: string, value: string): void => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private mode or blocked browser contexts.
  }
};

const readStoredIncomingMessageSoundEnabled = (): boolean => {
  const savedMode = readLocalStorageItem(SOUND_MODE_STORAGE_KEY);

  if (!isIncomingMessageSoundMode(savedMode)) {
    return DEFAULT_INCOMING_MESSAGE_SOUND_ENABLED;
  }

  return savedMode === 'sound';
};

const saveIncomingMessageSoundEnabled = (isEnabled: boolean): void => {
  writeLocalStorageItem(SOUND_MODE_STORAGE_KEY, isEnabled ? 'sound' : 'muted');
};

let isIncomingMessageSoundEnabledState =
  readStoredIncomingMessageSoundEnabled();

const listeners = new Set<() => void>();

const getSnapshot = () => isIncomingMessageSoundEnabledState;

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const notifyListeners = () => {
  listeners.forEach((listener) => {
    listener();
  });
};

const setIncomingMessageSoundEnabledState = (
  isIncomingMessageSoundEnabled: boolean,
  options: { shouldPersist: boolean } = { shouldPersist: true }
) => {
  if (options.shouldPersist) {
    saveIncomingMessageSoundEnabled(isIncomingMessageSoundEnabled);
  }

  if (isIncomingMessageSoundEnabledState === isIncomingMessageSoundEnabled) {
    return;
  }

  isIncomingMessageSoundEnabledState = isIncomingMessageSoundEnabled;
  notifyListeners();
};

export const useIncomingMessageSound = () => {
  const isIncomingMessageSoundEnabled = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot
  );

  const incomingMessageSoundEnabledChangeHandler = useCallback(
    (nextIsIncomingMessageSoundEnabled: boolean) => {
      setIncomingMessageSoundEnabledState(nextIsIncomingMessageSoundEnabled);
    },
    []
  );

  const incomingMessageSoundToggleHandler = useCallback(() => {
    setIncomingMessageSoundEnabledState(!isIncomingMessageSoundEnabledState);
  }, []);

  useEffect(() => {
    setIncomingMessageSoundEnabledState(
      readStoredIncomingMessageSoundEnabled(),
      {
        shouldPersist: false,
      }
    );

    if (!isBrowser()) return undefined;

    const storageHandler = (event: StorageEvent) => {
      if (event.key !== SOUND_MODE_STORAGE_KEY) return;

      setIncomingMessageSoundEnabledState(
        readStoredIncomingMessageSoundEnabled(),
        { shouldPersist: false }
      );
    };

    window.addEventListener('storage', storageHandler);

    return () => {
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  return {
    isIncomingMessageSoundEnabled,
    incomingMessageSoundEnabledChangeHandler,
    incomingMessageSoundToggleHandler,
  };
};
